import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, X } from "lucide-react";
import { phoneDigitsKey } from "@/utils/phone";

function toIntlDigits(phone) {
  const key = phoneDigitsKey(phone);
  return key ? `972${key}` : "";
}

/** תצוגה בלי מקפים — 0506492777 */
function formatDisplayPhone(phone) {
  const key = phoneDigitsKey(phone);
  if (!key || key.length < 9) {
    return String(phone || "").replace(/\D/g, "") || String(phone || "").trim();
  }
  return `0${key}`;
}

function WhatsAppIcon({ className = "h-6 w-6" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export default function SupportContactPopup({ show, phone, whatsapp, onClose }) {
  const phoneDigits = toIntlDigits(phone);
  const whatsappDigits = toIntlDigits(whatsapp || phone);
  const displayPhone = formatDisplayPhone(phone || whatsapp);
  const hasPhone = !!phoneDigits;
  const hasWhatsapp = !!whatsappDigits;

  const handleClose = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    onClose?.();
  };

  const openTel = () => {
    if (!hasPhone) return;
    window.location.href = `tel:+${phoneDigits}`;
  };

  const openWhatsapp = () => {
    if (!hasWhatsapp) return;
    const text = encodeURIComponent("שלום, נתקלתי בבעיה באפליקציית רשת ואשמח לעזרה");
    window.open(`https://wa.me/${whatsappDigits}?text=${text}`, "_blank", "noopener,noreferrer");
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
          dir="rtl"
          onClick={handleClose}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 28, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-sm overflow-hidden rounded-t-2xl sm:rounded-2xl bg-white border border-gray-100 shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="support-contact-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={handleClose}
              aria-label="סגור"
              className="absolute left-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="px-5 pb-5 pt-6">
              <h2
                id="support-contact-title"
                className="text-center text-lg font-bold text-gray-900"
                dir="rtl"
              >
                נתקלת בבעיה{"\u200F"}?
              </h2>
              <p className="mt-1 text-center text-sm text-gray-500">
                התקשרות או הודעה לתמיכה
              </p>

              {displayPhone ? (
                <p
                  className="mt-4 text-center text-lg font-semibold text-emerald-700 tabular-nums tracking-normal"
                  dir="ltr"
                >
                  {displayPhone}
                </p>
              ) : null}

              {!hasPhone && !hasWhatsapp ? (
                <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-center text-sm text-amber-900">
                  עדיין לא הוגדר מספר תמיכה במערכת. פנה למנהל המערכת.
                </div>
              ) : (
                <div className="mt-5 grid gap-2">
                  {hasWhatsapp ? (
                    <button
                      type="button"
                      onClick={openWhatsapp}
                      className="flex w-full items-center gap-3 rounded-xl bg-[#25D366] px-4 py-3 text-white active:opacity-90"
                    >
                      <WhatsAppIcon className="h-7 w-7 shrink-0" />
                      <span className="flex-1 text-right text-sm font-bold">שליחה בוואטסאפ</span>
                    </button>
                  ) : null}

                  {hasPhone ? (
                    <button
                      type="button"
                      onClick={openTel}
                      className="flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 text-gray-900 active:bg-gray-50"
                    >
                      <Phone className="h-6 w-6 shrink-0 text-emerald-700" />
                      <span className="flex-1 text-right text-sm font-bold">חיוג לטלפון</span>
                    </button>
                  ) : null}
                </div>
              )}

              <button
                type="button"
                onClick={handleClose}
                className="mt-4 w-full py-2 text-sm text-gray-400 hover:text-gray-600"
              >
                סגור
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
